import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { supabase } from '../../lib/supabase';
import { calculatePaces, EVENTS, ZONE_ORDER, parseTimeToSeconds, formatTime } from '../../lib/vdot';
import { isRelayEvent } from '../../lib/relays';
import { checkQualifiers, formatStdTime, QUAL_STANDARDS } from '../../lib/qualStandards';
import { TrainingLogModal } from '../athlete/TrainingLogModal';

function formatSegmentActual(seg) {
  if (!seg) return '';
  const splits = (seg.splits ?? []).filter(Boolean);
  switch (seg.type) {
    case 'interval': {
      const unit = seg.distUnit ?? 'm';
      const reps = seg.reps ?? splits.length;
      const repDists = Array.from({ length: reps }, (_, i) =>
        (seg.repDistances?.[i] !== undefined && seg.repDistances[i] !== '') ? seg.repDistances[i] : seg.distance
      );
      const allSame = repDists.every(d => String(d) === String(repDists[0]));
      const prefix = allSame
        ? (reps && repDists[0] ? `${reps}×${repDists[0]}${unit}` : seg.label ?? '')
        : repDists.map(d => `${d}${unit}`).join(', ');
      return splits.length ? `${prefix}: ${splits.join(', ')}` : prefix;
    }
    case 'timed_interval': {
      const reps = seg.reps ?? splits.length;
      const dur = seg.duration ? `${seg.duration}s` : '';
      const prefix = reps && dur ? `${reps}×${dur}` : seg.label ?? '';
      return splits.length ? `${prefix}: ${splits.join(', ')}` : prefix;
    }
    case 'continuous': {
      if (!seg.distance) return seg.label ?? '';
      const distLabel = `${seg.distance}${seg.distUnit ?? 'mi'}`;
      if (seg.time) {
        const totalSec = parseTimeToSeconds(seg.time);
        const dist = parseFloat(seg.distance);
        if (totalSec && dist) {
          const miles = (seg.distUnit === 'km') ? dist / 1.60934 : (seg.distUnit === 'm') ? dist / 1609.344 : dist;
          const paceSec = totalSec / miles;
          const pm = Math.floor(paceSec / 60);
          const ps = Math.round(paceSec % 60);
          return `${distLabel} (${pm}:${String(ps).padStart(2, '0')}/mi)`;
        }
      }
      return distLabel;
    }
    case 'checkbox':
      return seg.checked ? `✓ ${seg.label ?? ''}` : (seg.label ?? '');
    case 'xt':
      return seg.duration ? `${seg.duration} ${seg.label || 'XT'}` : (seg.label ?? 'XT');
    default:
      return seg.label ?? '';
  }
}

const TAB_STYLE_ACTIVE = {
  padding: '10px 20px', background: 'none', border: 'none',
  borderBottom: '3px solid var(--gold)', fontFamily: 'var(--font)',
  fontSize: 14, fontWeight: 700, color: 'var(--navy)', cursor: 'pointer', whiteSpace: 'nowrap',
};
const TAB_STYLE_INACTIVE = {
  padding: '10px 20px', background: 'none', border: 'none',
  borderBottom: '3px solid transparent', fontFamily: 'var(--font)',
  fontSize: 14, fontWeight: 500, color: 'var(--gray-500)', cursor: 'pointer', whiteSpace: 'nowrap',
};

const STATUS_OPTIONS = ['pending', 'approved', 'ordered', 'delivered', 'denied'];
const STATUS_STYLES = {
  pending:   { background: 'var(--gray-100)', color: 'var(--gray-500)' },
  approved:  { background: '#dbeafe', color: '#1d4ed8' },
  ordered:   { background: '#fef9c3', color: '#92400e' },
  delivered: { background: '#d1fae5', color: '#065f46' },
  denied:    { background: '#fee2e2', color: '#991b1b' },
};

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatRaceDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function parseRaceEvents(eventField) {
  if (!eventField) return [];
  try {
    const arr = JSON.parse(eventField);
    if (Array.isArray(arr)) return arr;
  } catch {}
  return [eventField];
}

function getEventLabels(eventField) {
  const evts = parseRaceEvents(eventField);
  if (!evts.length) return '';
  return evts.map(e => EVENTS[e]?.label ?? e).join(', ');
}

export function ProAthleteDetail() {
  const { id } = useParams();
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Remember last viewed pro athlete for tab navigation
  useEffect(() => {
    if (id) sessionStorage.setItem('apex_last_pro_athlete', id);
  }, [id]);

  // Benchmarks & Races
  const [benchmarks, setBenchmarks] = useState([]);
  const [races, setRaces] = useState([]);
  const [paces, setPaces] = useState(null);
  const [vdot, setVdot] = useState(null);
  const [bestBenchmarkId, setBestBenchmarkId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [benchmarkRows, setBenchmarkRows] = useState([{ event: 'mile', time: '', date: '', customDist: '', customUnit: 'mi' }]);
  const [showRaceForm, setShowRaceForm] = useState(false);
  const [editRaceId, setEditRaceId] = useState(null);
  const [raceName, setRaceName] = useState('');
  const [raceFormDate, setRaceFormDate] = useState('');
  const [raceEvents, setRaceEvents] = useState([]);
  const [raceLocation, setRaceLocation] = useState('');
  const [racePriority, setRacePriority] = useState('B');
  const [raceNotes, setRaceNotes] = useState('');
  const [raceSaving, setRaceSaving] = useState(false);
  const [raceError, setRaceError] = useState('');
  const [resultInputs, setResultInputs] = useState({});
  const [resultSaving, setResultSaving] = useState(null);
  const [resultErrors, setResultErrors] = useState({});
  const [savedResults, setSavedResults] = useState({});

  // Qualifiers
  const [qualifiers, setQualifiers] = useState([]);

  // Training log
  const now = new Date();
  const [logMonth, setLogMonth] = useState(now.getMonth());
  const [logYear, setLogYear] = useState(now.getFullYear());
  const [monthLogs, setMonthLogs] = useState({});
  const [planWorkouts, setPlanWorkouts] = useState({});
  const [logEditDay, setLogEditDay] = useState(null);
  const [logViewDay, setLogViewDay] = useState(null);

  // Gear requests
  const [gearRequests, setGearRequests] = useState([]);
  const [gearStatusSaving, setGearStatusSaving] = useState(null);
  const [gearAdminNotes, setGearAdminNotes] = useState({});

  // Media
  const [mediaFilter, setMediaFilter] = useState('all');
  const [proMedia, setProMedia] = useState([]);

  useEffect(() => {
    if (!id) return;
    fetchAthlete();
    fetchBenchmarks();
    fetchRaces();
    fetchGearRequests();
    fetchProMedia();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    fetchMonthLogs();
    fetchPlanWorkouts();
  }, [id, logMonth, logYear]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    recalcPaces(benchmarks);
  }, [benchmarks]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (benchmarks.length && athlete?.gender) {
      setQualifiers(checkQualifiers(benchmarks, athlete.gender));
    }
  }, [benchmarks, athlete]);

  async function fetchAthlete() {
    const [{ data: prof }, { data: ap }] = await Promise.all([
      supabase.from('profiles').select('id, full_name, email').eq('id', id).single(),
      supabase.from('athlete_profiles').select('*').eq('id', id).maybeSingle(),
    ]);
    setAthlete({ ...prof, ...ap });
    setLoading(false);
  }

  async function fetchBenchmarks() {
    const { data } = await supabase.from('benchmarks').select('*')
      .eq('athlete_id', id).order('race_date', { ascending: false });
    setBenchmarks(data ?? []);
  }

  async function fetchRaces() {
    const { data } = await supabase.from('races').select('*')
      .eq('athlete_id', id).order('race_date', { ascending: true });
    setRaces(data ?? []);
  }

  function recalcPaces(bmarks) {
    if (!bmarks || bmarks.length === 0) { setPaces(null); setVdot(null); setBestBenchmarkId(null); return; }
    let bestBmark = null;
    let bestVdotVal = -Infinity;
    for (const b of bmarks) {
      const result = calculatePaces(b.event, b.time_seconds);
      if (result && result.vdot > bestVdotVal) { bestVdotVal = result.vdot; bestBmark = b; }
    }
    setBestBenchmarkId(bestBmark?.id ?? null);
    const result = bestBmark ? calculatePaces(bestBmark.event, bestBmark.time_seconds) : null;
    if (result) { setPaces(result.paces); setVdot(result.vdot); }
    else { setPaces(null); setVdot(null); }
  }

  async function handleAddBenchmarks(e) {
    e.preventDefault();
    setError('');
    const valid = benchmarkRows.filter(r => r.time.trim());
    if (valid.length === 0) { setError('Enter at least one time.'); return; }
    setSaving(true);
    for (const row of valid) {
      const seconds = parseTimeToSeconds(row.time);
      if (!seconds) continue;
      await supabase.from('benchmarks').insert({
        athlete_id: id,
        event: row.event === 'other'
          ? (row.customDist ? `${parseFloat(row.customDist).toFixed(1)} ${row.customUnit}` : row.event)
          : row.event,
        time_seconds: seconds,
        race_date: row.date || null,
      });
    }
    setBenchmarkRows([{ event: 'mile', time: '', date: '', customDist: '', customUnit: 'mi' }]);
    await fetchBenchmarks();
    setSaving(false);
  }

  async function handleDeleteBenchmark(benchmarkId) {
    await supabase.from('benchmarks').delete().eq('id', benchmarkId);
    await fetchBenchmarks();
  }

  function resetRaceForm() {
    setEditRaceId(null); setRaceName(''); setRaceFormDate(''); setRaceEvents([]);
    setRaceLocation(''); setRacePriority('B'); setRaceNotes('');
    setShowRaceForm(false);
  }

  async function handleAddRace(e) {
    e.preventDefault();
    setRaceError('');
    setRaceSaving(true);
    const payload = {
      name: raceName, race_date: raceFormDate,
      event: raceEvents.length > 0 ? JSON.stringify(raceEvents) : null,
      location: raceLocation || null, priority: racePriority, notes: raceNotes || null,
    };
    let err;
    if (editRaceId) {
      ({ error: err } = await supabase.from('races').update(payload).eq('id', editRaceId));
    } else {
      ({ error: err } = await supabase.from('races').insert({ athlete_id: id, ...payload }));
    }
    if (err) { setRaceError(err.message); setRaceSaving(false); return; }
    resetRaceForm();
    await fetchRaces();
    setRaceSaving(false);
  }

  async function handleDeleteRace(raceId) {
    await supabase.from('races').delete().eq('id', raceId);
    setRaces(prev => prev.filter(r => r.id !== raceId));
  }

  async function handleLogResult(race, eventKey) {
    const inputKey = eventKey ? `${race.id}-${eventKey}` : race.id;
    const seconds = parseTimeToSeconds(resultInputs[inputKey] ?? '');
    if (!seconds) { setResultErrors(prev => ({ ...prev, [inputKey]: 'Enter a valid time' })); return; }
    setResultErrors(prev => ({ ...prev, [inputKey]: '' }));
    setResultSaving(inputKey);

    const events = parseRaceEvents(race.event);
    const isPrimary = !eventKey || eventKey === events[0];

    if (isPrimary) {
      await supabase.from('races').update({ result_time_seconds: seconds }).eq('id', race.id);
    }
    if (eventKey) {
      await supabase.from('benchmarks').insert({
        athlete_id: id, event: eventKey, time_seconds: seconds,
        race_date: race.race_date, notes: race.name,
      });
    }

    setSavedResults(prev => ({ ...prev, [inputKey]: formatTime(seconds) }));
    setResultInputs(prev => ({ ...prev, [inputKey]: '' }));
    setResultSaving(null);
    if (isPrimary) {
      await fetchRaces();
      await fetchBenchmarks();
    }
  }

  function renderResultInputs(race) {
    const events = parseRaceEvents(race.event);

    if (events.length === 0) {
      const inputKey = race.id;
      const alreadySaved = race.result_time_seconds || savedResults[inputKey];
      if (alreadySaved) {
        return <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--gold-dark)' }}>{savedResults[inputKey] ?? formatTime(race.result_time_seconds)}</div>;
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="form-input" style={{ width: 80, padding: '5px 8px', fontSize: 12 }}
              type="text" placeholder="Result"
              value={resultInputs[inputKey] ?? ''}
              onChange={e => setResultInputs(prev => ({ ...prev, [inputKey]: e.target.value }))} />
            <button className="btn btn-primary" style={{ width: 'auto', padding: '5px 12px', fontSize: 12 }}
              onClick={() => handleLogResult(race, null)} disabled={resultSaving === inputKey}>
              {resultSaving === inputKey ? '...' : 'Log'}
            </button>
          </div>
          {resultErrors[inputKey] && <div style={{ fontSize: 11, color: 'var(--red, #dc2626)' }}>{resultErrors[inputKey]}</div>}
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {events.map((eventKey, i) => {
          const inputKey = `${race.id}-${eventKey}`;
          const savedTime = savedResults[inputKey] ?? (i === 0 && race.result_time_seconds ? formatTime(race.result_time_seconds) : null);
          const label = EVENTS[eventKey]?.label ?? eventKey;
          if (savedTime) {
            return (
              <div key={eventKey} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>{label}:</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--gold-dark)' }}>{savedTime}</span>
              </div>
            );
          }
          return (
            <div key={eventKey} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--gray-400)', minWidth: 56 }}>{label}:</span>
                <input className="form-input" style={{ width: 80, padding: '5px 8px', fontSize: 12 }}
                  type="text" placeholder="Result"
                  value={resultInputs[inputKey] ?? ''}
                  onChange={e => setResultInputs(prev => ({ ...prev, [inputKey]: e.target.value }))} />
                <button className="btn btn-primary" style={{ width: 'auto', padding: '5px 12px', fontSize: 12 }}
                  onClick={() => handleLogResult(race, eventKey)} disabled={resultSaving === inputKey}>
                  {resultSaving === inputKey ? '...' : 'Log'}
                </button>
              </div>
              {resultErrors[inputKey] && <div style={{ fontSize: 11, color: 'var(--red, #dc2626)' }}>{resultErrors[inputKey]}</div>}
            </div>
          );
        })}
      </div>
    );
  }

  function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
  }

  function buildCalendarCells(year, month) {
    const rawFirstDay = new Date(year, month, 1).getDay();
    const firstDay = (rawFirstDay + 6) % 7;
    const daysInMonth = getDaysInMonth(year, month);
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    const monthStr = String(month + 1).padStart(2, '0');
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(`${year}-${monthStr}-${String(d).padStart(2, '0')}`);
    }
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }

  async function fetchMonthLogs() {
    const monthStr = String(logMonth + 1).padStart(2, '0');
    const start = `${logYear}-${monthStr}-01`;
    const end = `${logYear}-${monthStr}-${String(getDaysInMonth(logYear, logMonth)).padStart(2, '0')}`;
    const { data } = await supabase.from('training_logs')
      .select('id, log_date, total_miles, running_miles, xt_miles, segments, notes')
      .eq('athlete_id', id).gte('log_date', start).lte('log_date', end);
    const map = {};
    (data ?? []).forEach(l => { map[l.log_date] = l; });
    setMonthLogs(map);
  }

  async function fetchPlanWorkouts() {
    const monthStr = String(logMonth + 1).padStart(2, '0');
    const start = `${logYear}-${monthStr}-01`;
    const end = `${logYear}-${monthStr}-${String(getDaysInMonth(logYear, logMonth)).padStart(2, '0')}`;
    const { data: assigns } = await supabase.from('athlete_plan_assignments').select('plan_id').eq('athlete_id', id);
    if (!assigns?.length) { setPlanWorkouts({}); return; }
    const planIds = assigns.map(a => a.plan_id);
    const { data: plans } = await supabase.from('plans').select('id')
      .in('id', planIds).eq('month', logMonth + 1).eq('year', logYear);
    if (!plans?.length) { setPlanWorkouts({}); return; }
    const { data: workouts } = await supabase.from('plan_workouts')
      .select('workout_date, custom_description')
      .in('plan_id', plans.map(p => p.id)).gte('workout_date', start).lte('workout_date', end);
    const map = {};
    (workouts ?? []).forEach(w => { map[w.workout_date] = w.custom_description ?? ''; });
    setPlanWorkouts(map);
  }

  function handleAdminLogSaved(logData) {
    setMonthLogs(prev => ({ ...prev, [logData.log_date]: logData }));
    setLogEditDay(null);
  }

  async function fetchGearRequests() {
    const { data } = await supabase.from('pro_gear_requests').select('*')
      .eq('athlete_id', id).order('created_at', { ascending: false });
    if (data) {
      setGearRequests(data);
      const notes = {};
      data.forEach(r => { notes[r.id] = r.admin_notes ?? ''; });
      setGearAdminNotes(notes);
    }
  }

  async function handleGearStatusChange(reqId, newStatus) {
    setGearStatusSaving(reqId);
    await supabase.from('pro_gear_requests').update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', reqId);
    setGearRequests(prev => prev.map(r => r.id === reqId ? { ...r, status: newStatus } : r));
    setGearStatusSaving(null);
  }

  async function handleGearAdminNoteBlur(reqId) {
    await supabase.from('pro_gear_requests').update({ admin_notes: gearAdminNotes[reqId] ?? null }).eq('id', reqId);
  }

  async function fetchProMedia() {
    const { data } = await supabase.from('pro_media').select('*')
      .eq('athlete_id', id).order('created_at', { ascending: false });
    setProMedia(data ?? []);
  }

  async function handleMarkUsed(mediaId) {
    const usedAt = new Date().toISOString();
    await supabase.from('pro_media').update({ used_at: usedAt }).eq('id', mediaId);
    setProMedia(prev => prev.map(m => m.id === mediaId ? { ...m, used_at: usedAt } : m));
  }

  async function handleAdminDeleteMedia(mediaId) {
    await supabase.from('pro_media').delete().eq('id', mediaId);
    setProMedia(prev => prev.filter(m => m.id !== mediaId));
  }

  async function handleToggleArchive() {
    const newValue = !athlete?.archived;
    setArchiving(true);
    await supabase.from('athlete_profiles').update({ archived: newValue }).eq('id', id);
    setAthlete(prev => ({ ...prev, archived: newValue }));
    setArchiving(false);
  }

  const initials = athlete?.full_name
    ? athlete.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  if (loading) {
    return (
      <Layout>
        <div className="loading-screen" style={{ minHeight: 200 }}>
          <div className="loading-dot-row">
            <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
          </div>
        </div>
      </Layout>
    );
  }

  // ── Tab renderers ─────────────────────────────────────────────────

  function renderOverviewTab() {
    const todayStr = toDateStr(new Date());

    // Improvement chart: group benchmarks by event, sort by date
    const eventGroups = {};
    benchmarks.forEach(b => {
      if (!eventGroups[b.event]) eventGroups[b.event] = [];
      eventGroups[b.event].push(b);
    });
    Object.values(eventGroups).forEach(arr => arr.sort((a, b) => (a.race_date ?? '').localeCompare(b.race_date ?? '')));
    const chartEvents = Object.entries(eventGroups).filter(([, arr]) => arr.length >= 2);

    return (
      <>
        {/* Pro comments */}
        {athlete?.pro_comments && (
          <div className="section-card" style={{ marginBottom: 20, borderLeft: '3px solid var(--gold)' }}>
            <div className="section-card-header">
              <span className="section-card-title">Athlete Comments</span>
              <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>From athlete profile</span>
            </div>
            <div style={{ padding: '16px 24px' }}>
              <p style={{ fontSize: 14, color: 'var(--gray-700)', lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: 0 }}>
                {athlete.pro_comments}
              </p>
            </div>
          </div>
        )}

        {/* Qualifier achievements */}
        {qualifiers.length > 0 && (
          <div className="section-card" style={{ marginBottom: 20 }}>
            <div className="section-card-header">
              <span className="section-card-title">Qualification Standards Achieved</span>
            </div>
            <div style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {qualifiers.map((q, i) => (
                <div key={i} style={{
                  background: 'rgba(252,198,37,0.12)', border: '1px solid rgba(252,198,37,0.4)',
                  borderRadius: 'var(--radius-sm)', padding: '10px 16px',
                }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)' }}>
                    &#127942; {EVENTS[q.event]?.label ?? q.event}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                    {q.standardLabel} &middot; {formatTime(q.time_seconds)}
                    {q.race_date && ` &middot; ${formatRaceDate(q.race_date)}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pace table */}
        {paces && vdot && (
          <div className="section-card" style={{ marginBottom: 20 }}>
            <div className="section-card-header">
              <span className="section-card-title">Training Paces</span>
              <span className="vdot-badge">VDOT <span>{vdot}</span></span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gray-100)' }}>
                    {['Zone', 'Per Mile', 'Per KM', 'Range (mi)'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 700, color: 'var(--navy)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ZONE_ORDER.filter(z => paces[z]).map(zoneKey => {
                    const zone = paces[zoneKey];
                    return (
                      <tr key={zoneKey} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                        <td style={{ padding: '8px 16px', fontWeight: 600, color: 'var(--navy)' }}>{zone.label}</td>
                        <td style={{ padding: '8px 16px', fontWeight: 700, color: 'var(--gold-dark)' }}>{zone.perMile}</td>
                        <td style={{ padding: '8px 16px', color: 'var(--gray-500)' }}>{zone.perKm}</td>
                        <td style={{ padding: '8px 16px', color: 'var(--gray-500)', fontSize: 12 }}>{zone.rangeMile}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent benchmarks */}
        {benchmarks.length > 0 && (
          <div className="section-card" style={{ marginBottom: 20 }}>
            <div className="section-card-header">
              <span className="section-card-title">Recent Benchmarks</span>
              <button className="btn-xs btn-xs-ghost" onClick={() => setActiveTab('benchmarks')}>
                Manage All
              </button>
            </div>
            {benchmarks.slice(0, 8).map(b => (
              <div key={b.id} style={{ padding: '10px 24px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginRight: 8 }}>
                    {EVENTS[b.event]?.label ?? b.event}
                  </span>
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--gold-dark)' }}>{formatTime(b.time_seconds)}</div>
                {b.race_date && <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{formatRaceDate(b.race_date)}</div>}
              </div>
            ))}
          </div>
        )}

        {/* Improvement chart */}
        {chartEvents.length > 0 && (
          <div className="section-card" style={{ marginBottom: 20 }}>
            <div className="section-card-header">
              <span className="section-card-title">Improvement Over Time</span>
            </div>
            <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
              {chartEvents.map(([ev, arr]) => {
                const min = Math.min(...arr.map(b => b.time_seconds));
                const max = Math.max(...arr.map(b => b.time_seconds));
                const range = max - min || 1;
                return (
                  <div key={ev}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 8 }}>
                      {EVENTS[ev]?.label ?? ev}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 60 }}>
                      {arr.map((b, i) => {
                        const heightPct = 20 + ((max - b.time_seconds) / range) * 80;
                        return (
                          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: 2 }}>
                            <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{formatTime(b.time_seconds)}</div>
                            <div style={{
                              background: 'var(--navy)', borderRadius: 3,
                              height: `${heightPct}%`, width: '100%', minHeight: 8,
                            }} />
                            {b.race_date && (
                              <div style={{ fontSize: 9, color: 'var(--gray-400)', textAlign: 'center' }}>
                                {new Date(b.race_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  }

  function renderBenchmarksTab() {
    return (
      <div className="profile-grid">
        {/* Add Benchmarks */}
        <div>
          <div className="section-card">
            <div className="section-card-header">
              <span className="section-card-title">Add Benchmarks</span>
            </div>
            <div style={{ padding: '16px 24px' }}>
              <form onSubmit={handleAddBenchmarks}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                  {benchmarkRows.map((row, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <select className="form-input form-select"
                        style={{ padding: '6px 8px', fontSize: 12 }}
                        value={row.event}
                        onChange={e => setBenchmarkRows(rows => rows.map((r, j) => j === i ? { ...r, event: e.target.value } : r))}>
                        {Object.entries(EVENTS)
                          .filter(([key]) => !isRelayEvent(key))
                          .map(([key, ev]) => (
                          <option key={key} value={key}>{ev.label}</option>
                        ))}
                      </select>
                      {row.event === 'other' && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <input className="form-input" style={{ padding: '6px 8px', fontSize: 12, flex: 1 }}
                            type="number" min="0" step="0.1" placeholder="Distance"
                            value={row.customDist}
                            onChange={e => setBenchmarkRows(rows => rows.map((r, j) => j === i ? { ...r, customDist: e.target.value } : r))} />
                          <select className="form-input form-select" style={{ padding: '6px 8px', fontSize: 12, flex: '0 0 60px' }}
                            value={row.customUnit}
                            onChange={e => setBenchmarkRows(rows => rows.map((r, j) => j === i ? { ...r, customUnit: e.target.value } : r))}>
                            <option value="mi">mi</option>
                            <option value="km">km</option>
                          </select>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input className="form-input" style={{ padding: '6px 8px', fontSize: 12, flex: '0 0 80px' }}
                          type="text" placeholder="4:32.56" value={row.time}
                          onChange={e => setBenchmarkRows(rows => rows.map((r, j) => j === i ? { ...r, time: e.target.value } : r))} />
                        <input className="form-input" style={{ padding: '6px 8px', fontSize: 12, flex: 1 }}
                          type="date" value={row.date}
                          onChange={e => setBenchmarkRows(rows => rows.map((r, j) => j === i ? { ...r, date: e.target.value } : r))} />
                        <button type="button"
                          style={{ background: 'none', border: 'none', color: 'var(--gray-300)', cursor: 'pointer', fontSize: 18 }}
                          onClick={() => setBenchmarkRows(rows => rows.length > 1 ? rows.filter((_, j) => j !== i) : rows)}>
                          &times;
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button type="button" className="btn-xs btn-xs-ghost"
                    onClick={() => setBenchmarkRows(rows => [...rows, { event: 'mile', time: '', date: '', customDist: '', customUnit: 'mi' }])}>
                    + Add Row
                  </button>
                  {error && <span className="form-error" style={{ margin: 0 }}>{error}</span>}
                  <button className="btn btn-primary" type="submit" disabled={saving}
                    style={{ marginLeft: 'auto', width: 'auto', padding: '8px 20px' }}>
                    {saving ? 'Saving...' : 'Save Benchmarks'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {benchmarks.length > 0 && (
            <div className="section-card" style={{ marginTop: 16 }}>
              <div className="section-card-header">
                <span className="section-card-title">Benchmark History</span>
                {vdot && <span className="vdot-badge">VDOT <span>{vdot}</span></span>}
              </div>
              <div style={{ padding: '12px 16px' }}>
                {benchmarks.map(b => {
                  const bResult = calculatePaces(b.event, b.time_seconds);
                  const bVdot = bResult?.vdot ?? null;
                  const isActive = b.id === bestBenchmarkId;
                  return (
                    <div key={b.id} className={`benchmark-item${isActive ? ' benchmark-item--active' : ''}`}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span className="benchmark-item-event">{EVENTS[b.event]?.label ?? b.event}</span>
                          {isActive && <span className="badge badge-gold" style={{ fontSize: 10 }}>Best VDOT</span>}
                        </div>
                        {b.race_date && <div className="benchmark-item-date">{formatRaceDate(b.race_date)}</div>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span className="benchmark-item-time">{formatTime(b.time_seconds)}</span>
                        {bVdot && <span className="benchmark-item-vdot">VDOT {bVdot}</span>}
                        <button className="benchmark-delete-btn" onClick={() => handleDeleteBenchmark(b.id)} title="Delete">
                          &times;
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Races */}
        <div>
          <div className="section-card">
            <div className="section-card-header">
              <span className="section-card-title">Races</span>
              <button className="btn-xs btn-xs-ghost" onClick={() => { if (showRaceForm) resetRaceForm(); else setShowRaceForm(true); }}>
                {showRaceForm ? 'Cancel' : '+ Add Race'}
              </button>
            </div>

            {showRaceForm && (
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--gray-100)' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>{editRaceId ? 'Edit Race' : 'Add Race'}</div>
                <form onSubmit={handleAddRace}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Race Name</label>
                      <input className="form-input" type="text" value={raceName} onChange={e => setRaceName(e.target.value)} required placeholder="Boston Marathon" />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Date</label>
                        <input className="form-input" type="date" value={raceFormDate} onChange={e => setRaceFormDate(e.target.value)} required />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Priority</label>
                        <select className="form-input form-select" value={racePriority} onChange={e => setRacePriority(e.target.value)}>
                          <option value="A">A — Peak</option>
                          <option value="B">B — Target</option>
                          <option value="C">C — Training</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Events (optional — select one or more)</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, maxHeight: 140, overflowY: 'auto', padding: 8, border: '1px solid var(--gray-200)', borderRadius: 6 }}>
                        {Object.entries(EVENTS)
                          // Hide split pseudo-events from the race
                          // picker — those are derived from the
                          // relay + leg combo, not picked directly.
                          .filter(([key]) => !key.endsWith('-split'))
                          .map(([key, ev]) => (
                          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, cursor: 'pointer', padding: '2px 0' }}>
                            <input
                              type="checkbox"
                              checked={raceEvents.includes(key)}
                              onChange={e => setRaceEvents(prev =>
                                e.target.checked ? [...prev, key] : prev.filter(k => k !== key)
                              )}
                            />
                            {ev.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Location</label>
                      <input className="form-input" type="text" value={raceLocation} onChange={e => setRaceLocation(e.target.value)} placeholder="Boston, MA" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Notes</label>
                      <input className="form-input" type="text" value={raceNotes} onChange={e => setRaceNotes(e.target.value)} placeholder="Qualifying race for..." />
                    </div>
                  </div>
                  {raceError && <p className="form-error" style={{ marginTop: 8 }}>{raceError}</p>}
                  <button className="btn btn-primary" type="submit" disabled={raceSaving} style={{ marginTop: 12, width: 'auto', padding: '8px 20px' }}>
                    {raceSaving ? 'Saving...' : (editRaceId ? 'Update Race' : 'Add Race')}
                  </button>
                </form>
              </div>
            )}

            {races.length === 0 && !showRaceForm && (
              <div className="section-card-body">No races scheduled yet.</div>
            )}

            {races.map(race => (
              <div key={race.id} style={{ padding: '12px 24px', borderBottom: '1px solid var(--gray-100)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className={`race-priority-badge priority-${race.priority}`}>{race.priority}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)' }}>{race.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                      {getEventLabels(race.event) || null}
                      {getEventLabels(race.event) && ' · '}{formatRaceDate(race.race_date)}
                      {race.location && ` · ${race.location}`}
                    </div>
                  </div>
                  <button
                    className="btn-xs btn-xs-ghost"
                    style={{ fontSize: 11 }}
                    onClick={() => {
                      setRaceName(race.name); setRaceFormDate(race.race_date);
                      setRaceEvents(parseRaceEvents(race.event));
                      setRaceLocation(race.location ?? ''); setRacePriority(race.priority);
                      setRaceNotes(race.notes ?? ''); setEditRaceId(race.id); setShowRaceForm(true);
                    }}
                  >Edit</button>
                  {renderResultInputs(race)}
                  <button onClick={() => handleDeleteRace(race.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--gray-400)', cursor: 'pointer', fontSize: 18 }}>
                    &times;
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderTrainingLogTab() {
    const todayStr = toDateStr(new Date());
    const monthName = new Date(logYear, logMonth, 1).toLocaleString('en-US', { month: 'long' });
    const weeks = buildCalendarCells(logYear, logMonth);

    function goToPrevMonth() {
      if (logMonth === 0) { setLogMonth(11); setLogYear(y => y - 1); }
      else setLogMonth(m => m - 1);
    }
    function goToNextMonth() {
      if (logMonth === 11) { setLogMonth(0); setLogYear(y => y + 1); }
      else setLogMonth(m => m + 1);
    }

    return (
      <div className="section-card">
        <div className="section-card-header">
          <span className="section-card-title">Training Log</span>
          <div className="tl-cal-month-nav">
            <button className="tl-cal-nav-btn" onClick={goToPrevMonth}>&larr;</button>
            <span className="tl-cal-month-label">{monthName} {logYear}</span>
            <button className="tl-cal-nav-btn" onClick={goToNextMonth}>&rarr;</button>
          </div>
        </div>
        <div style={{ borderTop: '1px solid var(--gray-100)' }}>
          <div className="calendar-grid">
            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
              <div key={d} className="calendar-header-cell">{d}</div>
            ))}
          </div>
          {weeks.map((week, wi) => {
            const weekMiles = week.reduce((sum, dateStr) => {
              if (!dateStr) return sum;
              return sum + (monthLogs[dateStr]?.total_miles ?? 0);
            }, 0);
            return (
              <div key={wi} className="calendar-grid calendar-week-row">
                {week.map((dateStr, i) => {
                  if (!dateStr) return <div key={i} className="day-cell day-cell--outside" />;
                  const log = monthLogs[dateStr];
                  const planned = planWorkouts[dateStr];
                  const isToday = dateStr === todayStr;
                  const dayNum = parseInt(dateStr.slice(8), 10);
                  return (
                    <div
                      key={dateStr}
                      className={`day-cell${isToday ? ' day-cell--today' : ''}${log ? ' day-cell--logged' : ''}`}
                      onClick={() => setLogViewDay({ date: dateStr, log, planned })}
                    >
                      <div className="day-number">{dayNum}</div>
                      {log ? (
                        <>
                          <div className="day-preview">{(log.segments ?? []).map(s => formatSegmentActual(s)).filter(Boolean).join(' · ') || 'Workout'}</div>
                          {log.total_miles > 0 && <div className="tl-cal-miles">{log.total_miles.toFixed(1)} mi</div>}
                        </>
                      ) : planned ? (
                        <div className="day-preview day-preview--planned">{planned}</div>
                      ) : (
                        <div className="day-preview day-preview--rest">Rest</div>
                      )}
                    </div>
                  );
                })}
                <div className="week-volume-row-total">
                  Week: <strong>{weekMiles > 0.005 ? weekMiles.toFixed(1) + ' mi' : '0 mi'}</strong>
                </div>
              </div>
            );
          })}
          {(() => {
            const monthTotal = Object.values(monthLogs).reduce((sum, l) => sum + (l.total_miles ?? 0), 0);
            return (
              <div className="month-volume-total">
                Month total: <strong>{monthTotal > 0.005 ? monthTotal.toFixed(1) + ' mi' : '0 mi'}</strong>
              </div>
            );
          })()}
        </div>

        {/* Log view/edit popup */}
        {logViewDay && (
          <div className="day-modal-overlay" onClick={() => setLogViewDay(null)}>
            <div className="day-modal" onClick={e => e.stopPropagation()}>
              <div className="day-modal-header">
                <div>
                  <div className="day-modal-date">{formatRaceDate(logViewDay.date)}</div>
                  {logViewDay.planned && (
                    <div style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 2 }}>
                      Plan: {logViewDay.planned}
                    </div>
                  )}
                </div>
                <button onClick={() => setLogViewDay(null)}
                  style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--gray-400)' }}>
                  &times;
                </button>
              </div>
              <div className="day-modal-body">
                {logViewDay.log ? (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      {(logViewDay.log.segments ?? []).map((seg, si) => (
                        <div key={si} style={{ padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)' }}>{seg.label || 'Segment'}</div>
                          {seg.notes && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>{seg.notes}</div>}
                        </div>
                      ))}
                    </div>
                    {logViewDay.log.total_miles > 0 && (
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>
                        {(logViewDay.log.running_miles || 0) > 0.001 && (logViewDay.log.xt_miles || 0) > 0.001
                          ? `Run: ${logViewDay.log.running_miles.toFixed(2)} mi · XT: ${logViewDay.log.xt_miles.toFixed(2)} mi · Total: ${logViewDay.log.total_miles.toFixed(2)} mi`
                          : (logViewDay.log.xt_miles || 0) > 0.001 && (logViewDay.log.running_miles || 0) <= 0.001
                            ? `XT: ${logViewDay.log.xt_miles.toFixed(2)} mi`
                            : `Total: ${logViewDay.log.total_miles.toFixed(2)} mi`}
                      </div>
                    )}
                    <button className="btn btn-primary" style={{ width: 'auto', padding: '8px 20px' }}
                      onClick={() => { setLogEditDay(logViewDay); setLogViewDay(null); }}>
                      Edit Log
                    </button>
                  </>
                ) : (
                  <>
                    <p style={{ fontSize: 14, color: 'var(--gray-400)', marginBottom: 12 }}>No log entry for this day.</p>
                    <button className="btn btn-primary" style={{ width: 'auto', padding: '8px 20px' }}
                      onClick={() => { setLogEditDay(logViewDay); setLogViewDay(null); }}>
                      Add Log
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderGearTab() {
    return (
      <>
        <div style={{ marginBottom: 16 }}>
          <p className="page-eyebrow">Gear</p>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Gear Requests</h2>
        </div>

        {gearRequests.length === 0 ? (
          <div className="section-card">
            <div className="section-card-body">No gear requests yet.</div>
          </div>
        ) : (
          <div className="section-card">
            {gearRequests.map(req => (
              <div key={req.id} style={{ padding: '16px 24px', borderBottom: '1px solid var(--gray-100)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 2 }}>
                      {req.item_type}
                      {req.brand && <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}> &mdash; {req.brand}</span>}
                      {req.model && <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}> {req.model}</span>}
                    </div>
                    {req.size && <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>Size: {req.size}</div>}
                    {req.notes && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>{req.notes}</div>}
                    <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                      Requested {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 180 }}>
                    <div>
                      <label className="form-label" style={{ fontSize: 11 }}>Status</label>
                      <select
                        className="form-input form-select"
                        style={{ fontSize: 13, padding: '6px 10px', marginTop: 2 }}
                        value={req.status}
                        onChange={e => handleGearStatusChange(req.id, e.target.value)}
                        disabled={gearStatusSaving === req.id}
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: 11 }}>Admin Notes</label>
                      <textarea
                        className="form-input"
                        style={{ fontSize: 12, padding: '6px 8px', minHeight: 60, resize: 'vertical', marginTop: 2 }}
                        placeholder="Order details, tracking number..."
                        value={gearAdminNotes[req.id] ?? ''}
                        onChange={e => setGearAdminNotes(prev => ({ ...prev, [req.id]: e.target.value }))}
                        onBlur={() => handleGearAdminNoteBlur(req.id)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  function renderMediaTab() {
    const filtered = proMedia.filter(m => {
      if (mediaFilter === 'available') return !m.used_at;
      if (mediaFilter === 'used') return !!m.used_at;
      return true;
    });

    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p className="page-eyebrow">Media</p>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Media Gallery</h2>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['all', 'available', 'used'].map(f => (
              <button key={f} onClick={() => setMediaFilter(f)}
                style={{
                  padding: '6px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'var(--font)', border: 'none',
                  background: mediaFilter === f ? 'var(--navy)' : 'var(--gray-100)',
                  color: mediaFilter === f ? 'var(--white)' : 'var(--gray-700)',
                }}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="section-card">
            <div className="section-card-body">No media in this category.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {filtered.map(m => (
              <div key={m.id} style={{ borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '1px solid var(--gray-200)', background: 'var(--white)', boxShadow: 'var(--shadow-card)' }}>
                <div style={{ position: 'relative', paddingTop: '75%', background: 'var(--gray-100)' }}>
                  {/\.(mp4|mov|webm)$/i.test(m.file_url)
                    ? <video src={m.file_url} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} muted controls />
                    : <img src={m.file_url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                  }
                  {m.used_at && (
                    <div style={{
                      position: 'absolute', top: 8, right: 8,
                      background: 'rgba(6,95,70,0.9)', color: 'white',
                      borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 700,
                    }}>
                      Used
                    </div>
                  )}
                </div>
                <div style={{ padding: '10px 12px' }}>
                  {m.caption && (
                    <div style={{ fontSize: 13, color: 'var(--gray-700)', marginBottom: 8, lineHeight: 1.4 }}>{m.caption}</div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 8 }}>
                    Uploaded {new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {!m.used_at && (
                      <button className="btn btn-primary" style={{ fontSize: 11, padding: '5px 12px', width: 'auto', flex: 1 }}
                        onClick={() => handleMarkUsed(m.id)}>
                        Mark as Used
                      </button>
                    )}
                    <button onClick={() => handleAdminDeleteMedia(m.id)}
                      style={{ background: 'none', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-sm)', color: 'var(--gray-400)', cursor: 'pointer', fontSize: 12, padding: '5px 10px' }}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <Layout>
      <Link to="/admin/pro-athletes" className="back-link">&larr; Pro Athletes</Link>

      {/* Header */}
      <div className="profile-header">
        <div className="profile-avatar" style={{ cursor: 'default' }}>
          {athlete?.avatar_url
            ? <img src={athlete.avatar_url} alt={athlete?.full_name} />
            : initials
          }
        </div>
        <div className="profile-meta">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2>{athlete?.full_name}</h2>
            <span className="badge badge-gold" style={{ fontSize: 10, letterSpacing: '0.1em' }}>APEX ELITE PRO</span>
            {athlete?.archived && <span className="admin-plan-badge admin-plan-badge--missing">Archived</span>}
            {qualifiers.length > 0 && qualifiers.map((q, i) => (
              <span key={i} style={{ background: 'rgba(252,198,37,0.2)', color: '#92400e', borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                &#127942; {EVENTS[q.event]?.label ?? q.event}
              </span>
            ))}
          </div>
          <p>
            {athlete?.email}
            {athlete?.gender && ` \xb7 ${athlete.gender.charAt(0).toUpperCase() + athlete.gender.slice(1)}`}
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {vdot && <span className="vdot-badge">VDOT <span>{vdot}</span></span>}
          <button
            className="btn-xs btn-xs-ghost"
            style={athlete?.archived ? {} : { color: 'var(--gray-400)' }}
            onClick={handleToggleArchive}
            disabled={archiving}
          >
            {archiving ? '…' : athlete?.archived ? 'Restore' : 'Archive'}
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--gray-200)', marginBottom: 24, overflowX: 'auto' }}>
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'benchmarks', label: 'Benchmarks & Races' },
          { key: 'training', label: 'Training Log' },
          { key: 'gear', label: `Gear Requests${gearRequests.filter(r => r.status === 'pending').length > 0 ? ` (${gearRequests.filter(r => r.status === 'pending').length})` : ''}` },
          { key: 'media', label: `Media${proMedia.filter(m => !m.used_at).length > 0 ? ` (${proMedia.filter(m => !m.used_at).length})` : ''}` },
        ].map(tab => (
          <button key={tab.key} style={activeTab === tab.key ? TAB_STYLE_ACTIVE : TAB_STYLE_INACTIVE}
            onClick={() => setActiveTab(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && renderOverviewTab()}
      {activeTab === 'benchmarks' && renderBenchmarksTab()}
      {activeTab === 'training' && renderTrainingLogTab()}
      {activeTab === 'gear' && renderGearTab()}
      {activeTab === 'media' && renderMediaTab()}

      {/* Training log edit modal */}
      {logEditDay && (
        <TrainingLogModal
          day={{ date: logEditDay.date, dayLabel: new Date(logEditDay.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' }), dayNum: parseInt(logEditDay.date.slice(8), 10), workout: logEditDay.planned ?? '', rawWorkout: logEditDay.planned ?? '' }}
          paces={paces}
          existingLog={logEditDay.log ?? null}
          onSave={handleAdminLogSaved}
          onClose={() => setLogEditDay(null)}
          athleteId={id}
        />
      )}
    </Layout>
  );
}
